import React from 'react';
import { Monitor, Layers, Camera, Grid3x3 } from 'lucide-react';
import { NodeType } from '@/types';
import {
  AspectRatioGrid, ResolutionSelector, ParamSection,
  IMAGE_ASPECT_RATIOS, VIDEO_ASPECT_RATIOS, IMAGE_RESOLUTIONS, UPSCALE_RESOLUTIONS,
  VIDEO_RESOLUTIONS, IMAGE_COUNTS,
} from '../ParamSelectors';

interface AdvancedParamsEditorProps {
  nodeId: string;
  nodeType: NodeType;
  aspectRatio?: string;
  resolution?: string;
  duration?: number;
  imageCount?: number;
  videoCount?: number;
  upscaleStrength?: number;
  generateAudio?: boolean;
  generationMode?: string;
  modelSupportedResolutions?: string[];
  onUpdate: (id: string, data: any) => void;
}

export const AdvancedParamsEditor: React.FC<AdvancedParamsEditorProps> = ({
  nodeId,
  nodeType,
  aspectRatio,
  resolution,
  duration,
  imageCount,
  videoCount,
  upscaleStrength,
  generateAudio,
  generationMode,
  modelSupportedResolutions = [],
  onUpdate,
}) => {
  const isUpscaleMode = generationMode === 'UPSCALE';
  const showImageCount = nodeType.includes('IMAGE') && !isUpscaleMode;

  const getAvailableResolutions = (): string[] => {
    if (isUpscaleMode) return UPSCALE_RESOLUTIONS;
    if (nodeType.includes('VIDEO')) return VIDEO_RESOLUTIONS;
    if (modelSupportedResolutions.length > 0) return modelSupportedResolutions;
    return IMAGE_RESOLUTIONS;
  };

  return (
    <>
      {nodeType === NodeType.IMAGE_GENERATOR && (
        <div className="relative group/image-params">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white/60 hover:text-white">
            <Monitor size={12} />
            <span>{aspectRatio || '16:9'} · {(resolution || '1k').toUpperCase()}</span>
          </div>
          <div className="absolute bottom-full left-0 pb-2 w-[260px] opacity-0 translate-y-2 pointer-events-none group-hover/image-params:opacity-100 group-hover/image-params:translate-y-0 group-hover/image-params:pointer-events-auto transition-all duration-200 z-[200]">
            <div className="bg-[#1c1c1e] border border-white/10 rounded-xl shadow-xl overflow-hidden p-3">
              <ParamSection label="分辨率">
                <ResolutionSelector resolutions={getAvailableResolutions()} allResolutions={IMAGE_RESOLUTIONS} activeResolution={resolution} onSelect={(r) => onUpdate(nodeId, { resolution: r })} />
              </ParamSection>
              <ParamSection label="比例" className="mb-0">
                <AspectRatioGrid ratios={IMAGE_ASPECT_RATIOS} activeRatio={aspectRatio} onSelect={(r) => onUpdate(nodeId, { aspectRatio: r })} />
              </ParamSection>
            </div>
          </div>
        </div>
      )}

      {nodeType === NodeType.IMAGE_GENERATOR && (
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white/60 hover:text-white" title="摄像机控制">
          <Camera size={12} />
          <span>摄像机控制</span>
        </button>
      )}

      {nodeType === NodeType.PANORAMA_GENERATOR && (
        <div className="relative group/panorama-params">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white/60 hover:text-white">
            <Monitor size={12} />
            <span>{aspectRatio || '2:1'} · {(resolution || '1k').toUpperCase()}</span>
          </div>
          <div className="absolute bottom-full left-0 pb-2 w-[260px] opacity-0 translate-y-2 pointer-events-none group-hover/panorama-params:opacity-100 group-hover/panorama-params:translate-y-0 group-hover/panorama-params:pointer-events-auto transition-all duration-200 z-[200]">
            <div className="bg-[#1c1c1e] border border-white/10 rounded-xl shadow-xl overflow-hidden p-3">
              <ParamSection label="比例（全景固定 2:1）">
                <div className="flex gap-1.5">
                  <button
                    onClick={() => onUpdate(nodeId, { aspectRatio: '2:1' })}
                    className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${aspectRatio === '2:1' ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
                  >
                    2:1（Equirectangular）
                  </button>
                </div>
              </ParamSection>
              <ParamSection label="输出分辨率" className="mb-0">
                <ResolutionSelector resolutions={IMAGE_RESOLUTIONS} allResolutions={IMAGE_RESOLUTIONS} activeResolution={resolution} onSelect={(r) => onUpdate(nodeId, { resolution: r })} />
                <div className="mt-1.5 text-[9px] text-white/25 leading-relaxed">
                  1K≈1024×512 · 2K≈2048×1024 · 4K≈3840×1920
                </div>
              </ParamSection>
            </div>
          </div>
        </div>
      )}

      {nodeType === NodeType.VIDEO_GENERATOR && (
        <div className="relative group/video-params">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white/60 hover:text-white">
            <Monitor size={12} />
            <span>{aspectRatio || '16:9'} · {(resolution || '720p').toUpperCase()} · {duration || 5}s{generateAudio ? ' · 🔊' : ''}</span>
          </div>
          <div className="absolute bottom-full left-0 pb-2 w-[260px] opacity-0 translate-y-2 pointer-events-none group-hover/video-params:opacity-100 group-hover/video-params:translate-y-0 group-hover/video-params:pointer-events-auto transition-all duration-200 z-[200]">
            <div className="bg-[#1c1c1e] border border-white/10 rounded-xl shadow-xl overflow-hidden p-3">
              <ParamSection label="比例">
                <AspectRatioGrid ratios={VIDEO_ASPECT_RATIOS} activeRatio={aspectRatio} onSelect={(r) => onUpdate(nodeId, { aspectRatio: r })} />
              </ParamSection>
              <ParamSection label="清晰度">
                <ResolutionSelector resolutions={VIDEO_RESOLUTIONS} activeResolution={resolution} onSelect={(r) => onUpdate(nodeId, { resolution: r })} />
              </ParamSection>
              <ParamSection label="视频时长">
                <div className="flex items-center gap-3 px-1">
                  <input
                    type="range"
                    min="5"
                    max="20"
                    step="5"
                    value={duration || 5}
                    onChange={(e) => onUpdate(nodeId, { duration: parseInt(e.target.value) })}
                    className="flex-1 h-1 bg-white/10 rounded-full appearance-none cursor-pointer accent-white"
                    style={{ accentColor: 'rgba(255,255,255,0.8)' }}
                  />
                  <span className="text-[11px] text-white/60 w-6 text-right">{duration || 5}s</span>
                </div>
              </ParamSection>
              <ParamSection label="生成音频" className="mb-0">
                <div className="flex gap-1.5">
                  <button
                    onClick={() => onUpdate(nodeId, { generateAudio: true })}
                    className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${generateAudio === true ? 'bg-white/15 text-white border-white/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
                  >
                    开启
                  </button>
                  <button
                    onClick={() => onUpdate(nodeId, { generateAudio: false })}
                    className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${generateAudio !== true ? 'bg-white/15 text-white border-white/30' : 'text-white/50 border-white/10 hover:border-white/20 hover:bg-white/5'}`}
                  >
                    关闭
                  </button>
                </div>
              </ParamSection>
            </div>
          </div>
        </div>
      )}

      {(showImageCount || nodeType === NodeType.VIDEO_GENERATOR || nodeType === NodeType.PANORAMA_GENERATOR) && (
        <div className="relative group/count">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer transition-colors text-xs font-medium text-white/60 hover:text-white">
            <Layers size={12} />
            <span>{nodeType === NodeType.PANORAMA_GENERATOR ? `${imageCount || 1}张` : nodeType.includes('IMAGE') ? `${imageCount || 1}张` : `${videoCount || 1}个`}</span>
          </div>
          <div className="absolute bottom-full left-0 pb-2 w-20 opacity-0 translate-y-2 pointer-events-none group-hover/count:opacity-100 group-hover/count:translate-y-0 group-hover/count:pointer-events-auto transition-all duration-200 z-[200]">
            <div className="bg-[#1c1c1e] border border-white/10 rounded-xl shadow-xl overflow-hidden">
              {IMAGE_COUNTS.map(c => (
                <div
                  key={c}
                  onClick={() => onUpdate(nodeId, { imageCount: c })}
                  className={`px-3 py-2 text-xs font-medium cursor-default hover:bg-white/10 ${(imageCount || 1) === c ? 'text-white bg-white/5' : 'text-white/60'}`}
                >
                  {`${c}张`}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {nodeType === NodeType.PANORAMA_GENERATOR && (
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500/20 border border-teal-500/30 text-teal-400 text-xs font-medium cursor-default">
          <Grid3x3 size={12} />
          <span>全景</span>
        </button>
      )}
    </>
  );
};
